/**
 * useSuggestions hook
 *
 * Fetches and manages PendingSuggestion records for the authenticated user.
 * Exposes accept, modify, and reject actions that commit profile data before
 * updating suggestion status.
 *
 * For skill_rating suggestions, acceptance is delegated to the Placement Engine
 * which handles matching, confirmation dialogs, and package creation flows.
 */

import { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from "@backend-schema";
import type { PendingSuggestion, SuggestionPayload } from '../types/suggestions';
import type { PlacementResult, PackageSkillEntry } from '../types/placement';
import {
  parsePayload,
  validateSkillPackagePayload,
} from '../utils/suggestion-payload';
import { usePlacementEngine } from './usePlacementEngine';

const client = generateClient<Schema>();

export interface UseSuggestionsReturn {
  suggestions: PendingSuggestion[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  acceptSuggestion: (id: string) => Promise<void>;
  modifySuggestion: (id: string, payload: SuggestionPayload) => Promise<void>;
  rejectSuggestion: (id: string) => Promise<void>;
  // Placement engine state for UI
  placementResult: PlacementResult | null;
  pendingSkillName: string;
  isSearching: boolean;
  searchStatus: string;
  placementError: string | null;
  allPackageSkills: PackageSkillEntry[];
  confirmPlacement: () => Promise<void>;
  rejectPlacement: () => void;
  dismissCreation: () => void;
  createPackageAndPlace: (packageName: string, categoryName: string) => Promise<void>;
  placeInExistingCategory: (categoryId: string, packageId: string, packageName: string, categoryName: string) => Promise<void>;
}

// ─── Commit helpers ───────────────────────────────────────────────────────────

async function commitSkillPackage(payload: SuggestionPayload): Promise<void> {
  if (!validateSkillPackagePayload(payload)) {
    throw new Error('Invalid skill package payload');
  }
  const { name, description, categories } = payload;

  // Create the package
  const pkgResult = await client.models.CustomSkillPackages.create({
    name,
    ...(description ? { description } : {}),
  });
  if (pkgResult.errors?.length) {
    throw new Error(pkgResult.errors.map((e) => e.message).join(', '));
  }
  const pkgId = pkgResult.data?.id;
  if (!pkgId) throw new Error('Failed to create skill package — no ID returned');

  // Create categories and skills
  for (const cat of categories) {
    const catResult = await client.models.CustomSkillCategories.create({
      name: cat.name,
      customPackageId: pkgId,
    });
    if (catResult.errors?.length) {
      throw new Error(catResult.errors.map((e) => e.message).join(', '));
    }
    const catId = catResult.data?.id;
    if (!catId) throw new Error(`Failed to create category "${cat.name}" — no ID returned`);

    await Promise.all(
      cat.skills.map((skillName) =>
        client.models.CustomSkills.create({ name: skillName, customCategoryId: catId })
      )
    );
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSuggestions(): UseSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<PendingSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const placementEngine = usePlacementEngine();
  const {
    confirmPlacement: engineConfirm,
    createPackageAndPlace: engineCreate,
    placeInExistingCategory: enginePlaceInCategory,
    startPlacement: engineStart,
    placementResult: enginePlacementResult,
  } = placementEngine;  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.models.PendingSuggestion.list({
        filter: { status: { eq: 'pending' } },
      });
      console.log('[useSuggestions] query result:', {
        dataCount: result.data?.length ?? 0,
        errors: result.errors,
        firstItem: result.data?.[0] ? { id: result.data[0].id, source: result.data[0].source, owner: result.data[0].owner } : null,
      });
      if (result.errors?.length) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }
      // Sort by createdAt descending (most recent first)
      const sorted = [...(result.data ?? [])].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      setSuggestions(sorted as PendingSuggestion[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const acceptSuggestion = useCallback(
    async (id: string) => {
      const suggestion = suggestions.find((s) => s.id === id);
      if (!suggestion) throw new Error('Suggestion not found');

      if (suggestion.suggestionType === 'skill_rating') {
        console.log('[acceptSuggestion] starting placement for:', suggestion.id);
        await engineStart(suggestion);
        console.log('[acceptSuggestion] after startPlacement, placementResult:', enginePlacementResult);
        if (enginePlacementResult?.matchType === 'exact') {
          await fetchSuggestions();
        }
      } else if (suggestion.suggestionType === 'skill_package') {
        const payload = parsePayload(suggestion.payload);
        await commitSkillPackage(payload);

        // Mark as accepted
        const result = await client.models.PendingSuggestion.update({
          id,
          status: 'accepted',
        });
        if (result.errors?.length) {
          throw new Error(result.errors.map((e) => e.message).join(', '));
        }

        await fetchSuggestions();
      }
    },
    [suggestions, fetchSuggestions, engineStart, enginePlacementResult]
  );

  const modifySuggestion = useCallback(
    async (id: string, modifiedPayload: SuggestionPayload) => {
      const suggestion = suggestions.find((s) => s.id === id);
      if (!suggestion) throw new Error('Suggestion not found');

      if (suggestion.suggestionType === 'skill_rating') {
        // Update the suggestion payload first, then delegate to placement engine
        const updateResult = await client.models.PendingSuggestion.update({
          id,
          payload: JSON.stringify(modifiedPayload),
        });
        if (updateResult.errors?.length) {
          throw new Error(updateResult.errors.map((e) => e.message).join(', '));
        }
        const updatedSuggestion: PendingSuggestion = {
          ...suggestion,
          payload: JSON.stringify(modifiedPayload),
        };
        await engineStart(updatedSuggestion);
        await fetchSuggestions();
      } else if (suggestion.suggestionType === 'skill_package') {
        await commitSkillPackage(modifiedPayload);

        // Mark as accepted (modify is accept with changes)
        const result = await client.models.PendingSuggestion.update({
          id,
          status: 'accepted',
          payload: JSON.stringify(modifiedPayload),
        });
        if (result.errors?.length) {
          throw new Error(result.errors.map((e) => e.message).join(', '));
        }

        await fetchSuggestions();
      }
    },
    [suggestions, fetchSuggestions, engineStart]
  );

  const rejectSuggestion = useCallback(
    async (id: string) => {
      // Reject: update status only — do NOT write any profile data
      const result = await client.models.PendingSuggestion.update({
        id,
        status: 'rejected',
      });
      if (result.errors?.length) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }
      await fetchSuggestions();
    },
    [fetchSuggestions]
  );

  return {
    suggestions,
    loading,
    error,
    refetch: fetchSuggestions,
    acceptSuggestion,
    modifySuggestion,
    rejectSuggestion,
    // Placement engine state for UI dialogs
    placementResult: placementEngine.placementResult,
    pendingSkillName: placementEngine.pendingSkillName,
    isSearching: placementEngine.isSearching,
    searchStatus: placementEngine.searchStatus,
    placementError: placementEngine.error,
    allPackageSkills: placementEngine.allPackageSkills,
    rejectPlacement: placementEngine.rejectPlacement,
    dismissCreation: placementEngine.dismissCreation,
    // Wrap confirm and createPackage to refresh the list after completion
    confirmPlacement: useCallback(async () => {
      await engineConfirm();
      await fetchSuggestions();
    }, [engineConfirm, fetchSuggestions]),
    createPackageAndPlace: useCallback(async (packageName: string, categoryName: string) => {
      await engineCreate(packageName, categoryName);
      await fetchSuggestions();
    }, [engineCreate, fetchSuggestions]),
    placeInExistingCategory: useCallback(async (categoryId: string, packageId: string, packageName: string, categoryName: string) => {
      await enginePlaceInCategory(categoryId, packageId, packageName, categoryName);
      await fetchSuggestions();
    }, [enginePlaceInCategory, fetchSuggestions]),
  };
}
